import { useSortable } from '@dnd-kit/sortable';
import type { ReactNode } from 'react';
import { CSS } from '@dnd-kit/utilities';
import { StyledSwitch } from 'components/inputs/Switch';
import { CheckboxWrapper } from './CheckboxWrapper';
import styled from '@emotion/styled';
import { Text } from 'components/text/Text';
import { Tooltip } from '@mui/material';
import { t } from '@lingui/macro';
import { ColumnSelectorItem } from '../../types';
import { trans } from 'features/misc/table/utils';

export const SortableItem = ({
  id,
  getIsVisible,
  getToggleVisibilityHandler,
  isDisabled,
  columnDef,
  tableId,
}: ColumnSelectorItem & { tableId?: string }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id,
    disabled: isDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const headerText = () => {
    let text: string | ReactNode = id;
    try {
      if (typeof columnDef.header === 'string') {
        text = trans(columnDef.header);
      }
      if (typeof columnDef.header === 'function') {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        text = columnDef.header({});
      }
    } catch (error) {
      text = id;
    }
    return text;
  };

  const text = headerText();

  const content = (
    <CheckboxWrapper hasIcon={!isDisabled} attributes={attributes} listeners={listeners}>
      <SwitchSlot>
        <StyledSwitch
          size="small"
          disabled={isDisabled}
          checked={getIsVisible()}
          onChange={getToggleVisibilityHandler()}
          data-testid={`table_${tableId}_column_selector_popover_list_general_item_${id}_switch`}
        />
      </SwitchSlot>
      <TextSlot>
        <Tooltip title={text} placement="top">
          <Label
            data-testid={`table_${tableId}_column_selector_popover_list_general_item_${id}_title`}
            variant="labelSm"
            isDisabled={isDisabled}
          >
            {text}
          </Label>
        </Tooltip>
      </TextSlot>
    </CheckboxWrapper>
  );

  return (
    <div ref={setNodeRef} style={style}>
      {isDisabled ? (
        <Tooltip title={t`This column cannot be hidden`} placement="top" arrow>
          <div>{content}</div>
        </Tooltip>
      ) : (
        content
      )}
    </div>
  );
};

interface LabelProps {
  isDisabled?: boolean;
}

/** Keeps the switch width fixed so long labels do not push it sideways */
const SwitchSlot = styled.span`
  flex-shrink: 0;
`;

/** Flex item that may shrink below intrinsic text width (ellipsis inside) */
const TextSlot = styled.span`
  flex: 1 1 0%;
  min-width: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
`;

const Label = styled(Text)<LabelProps>`
  ${p => p.isDisabled && `color: ${p.theme.palette.text.disabled};`}
  display: block;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1;
`;
